import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  getMachines,
  getMachineDepartments,
  readServiceTicketsByMachineId,
  readServiceTicketMessagesByMachineId,
  getTotalSVCHoursByMachine,
  getTotalServiceHoursByTicketCategoryAndYear,
  getMachineServiceHistory
} from "features/service/service.slice";
import {
  Stack,
  Paper,
  Typography,
  Button,
  Card,
  Tooltip,
  useTheme,
  Box,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { QrCode as QRCodeIcon, Download as DownloadIcon, FileUpload as FileUploadIcon } from "@mui/icons-material";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  Label,
  LabelList,
  Cell,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";
import Header from "component/Header";
import DataGridTable from "component/DataGridTable";
import AddEditMachine from "pages/Assets/Machines/AddEditMachine";
import { Link } from "react-router-dom";
import { Links } from "utils/pageLinkNames.js";
import { ZoomIn as ViewIcon } from "@mui/icons-material";
import AuthorizedImage from "component/AuthorizedImage";
import ViewImageVideoModal from "component/ViewImageVideoModal";
import { exportToCsv } from "utils/utilFunctions";
import TableSkeleton from "component/Loaders/TableSkeleton";
import GraphSkeleton from "component/Loaders/GraphSkeleton";
import { toast } from "react-toastify";
import LinearProgress from '@mui/material/LinearProgress';
import SendIcon from '@mui/icons-material/Send';

const Machine = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const [params, setParams] = useState(null);

  const { state } = useLocation();
  const { id } = useParams();
  const [initialFormData, setInitialFormData] = useState({
    serialNumber: "",
  });
  const [serviceTickets, setServiceTickets] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [enableEditing, setEnableEditing] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const [totalSVCHours, setTotalSVCHours] = useState([]);
  const [totalSVCHoursByCategoryAndYear, setTotalSVCHoursByCategoryAndYear] = useState([]);
  const [filters, setFilters] = useState({ engineer: "", category: "", status: "", supervisor: "" });

  const {
    machinesData,
    appConstants,
    machineDepartments,
    serviceTicketMachine,
    serviceTicketMessagesByMachineId,
    isLoading,
    totalSVCHoursByMachine,
    totalServiceHoursByCategoryAndYear,
  } = useSelector((state) => state.service);
  const { mode } = useSelector((state) => state.globalState);

  const [showDialogView, setShowDialogView] = useState(false);

  const [currentFilePathIndex, setCurrentFilePathIndex] = useState(0);
  const [serviceTicketMessagesList, setServiceTicketMessagesList] = useState([]);

  const [machineHistoryLoading, setMachineHistoryLoading] = useState(false);

  useEffect(() => {
    if (serviceTicketMachine) {
      const modified = serviceTicketMachine.map((ticket) => {
        return {
          ...ticket,
          ticketNumber: appConstants?.showServiceTicketId ? ticket.ticketId : ticket.serviceTicketNumber,
          dateCreated: moment(ticket.dateCreated).format("DD MMM YYYY"),
        };
      });
      setServiceTickets(modified?.sort((a, b) => b.ticketId - a.ticketId) || []);
    }
  }, [serviceTicketMachine, appConstants]);

  useEffect(() => {
    dispatch(getMachineDepartments());
    dispatch(getMachines());
    if (!id || state?.enableEditing) setEnableEditing(true);
  }, [dispatch, id, state?.enableEditing]);

  useEffect(() => {
    if (id && machinesData.length && machineDepartments.length) {
      const info = machinesData.find((machine) => machine.id == id);
      if (!info) return;
      setInitialFormData({ ...info, location: info.department || info.location || "" });
    }
  }, [id, machinesData, machineDepartments.length]);

  useEffect(() => {
    if (appConstants && appConstants?.allTypes) {
      dispatch(readServiceTicketsByMachineId({ ticketId: id, allAppUsers: appConstants.allAppUsers }));
      if (serviceTicketMessagesByMachineId) {
        const imageVideoPdfList = serviceTicketMessagesByMachineId.filter(
          (message) =>
            message.type === appConstants?.allTypes?.TYPE_OF_CONTENT.IMAGE ||
            message.type === appConstants?.allTypes?.TYPE_OF_CONTENT.VIDEO ||
            message.type === appConstants?.allTypes?.TYPE_OF_CONTENT.PDF
        );
        setServiceTicketMessagesList(imageVideoPdfList || []);
      }
    }
  }, [appConstants, serviceTicketMessagesByMachineId]);

  useEffect(() => {
    dispatch(getTotalServiceHoursByTicketCategoryAndYear(id));
    dispatch(getTotalSVCHoursByMachine(id));
    dispatch(readServiceTicketMessagesByMachineId(id));
  }, [dispatch]);

  useEffect(() => {
    if (totalServiceHoursByCategoryAndYear.data) {
      const modified = Object.keys(totalServiceHoursByCategoryAndYear.data).map((year) => {
        const categories = totalServiceHoursByCategoryAndYear.data[year].reduce((acc, { ticket_category, total_hours }) => {
          acc[ticket_category] = total_hours; // Add each category and its total_hours to the accumulator
          return acc;
        }, {});

        return { ...categories, year: Number(year) }; // Add the year to the result
      });

      setTotalSVCHoursByCategoryAndYear(modified);
    }
  }, [totalServiceHoursByCategoryAndYear]);

  useEffect(() => {
    if (totalSVCHoursByMachine.data && appConstants) {
      const { ABBREVIATIONS } = appConstants?.allTypes;
      const hoursByMachine = Object.values(appConstants.allTypes.SERVICE_CATEGORY)
        .map((category) => {
          const totalHourSpent = parseFloat(
            totalSVCHoursByMachine.data.find((item) => item.ticketCategory === category)?.totalHourSpent || 0
          );
          return {
            abbreviatedCategory: ABBREVIATIONS[category],
            category,
            totalHourSpent,
          };
        })
        .sort((a, b) => a.category.localeCompare(b.category));
      setTotalSVCHours(hoursByMachine);
    }
  }, [totalSVCHoursByMachine, appConstants]);

  useEffect(() => {
    if (totalServiceHoursByCategoryAndYear.data) {
      const modified = Object.keys(totalServiceHoursByCategoryAndYear.data).map((year) => {
        const categories = totalServiceHoursByCategoryAndYear.data[year].reduce((acc, { ticket_category, total_hours }) => {
          acc[ticket_category] = total_hours; // Add each category and its total_hours to the accumulator
          return acc;
        }, {});

        return { ...categories, year: Number(year) }; // Add the year to the result
      });

      setTotalSVCHoursByCategoryAndYear(modified);
    }
  }, [totalServiceHoursByCategoryAndYear]);

  useEffect(() => {
    if (totalSVCHoursByMachine.data && appConstants) {
      const { ABBREVIATIONS } = appConstants?.allTypes;
      const hoursByMachine = Object.values(appConstants.allTypes.SERVICE_CATEGORY)
        .filter(
          (category) =>
            category !== appConstants.allTypes.SERVICE_CATEGORY.INTERNAL_AUDIT && category !== appConstants.allTypes.SERVICE_CATEGORY.TASK
        )
        .map((category) => {
          const totalHourSpent = parseFloat(
            totalSVCHoursByMachine.data.find((item) => item.ticketCategory === category)?.totalHourSpent || 0
          );
          return {
            abbreviatedCategory: ABBREVIATIONS[category],
            category,
            totalHourSpent,
          };
        })
        .sort((a, b) => a.category.localeCompare(b.category));
      setTotalSVCHours(hoursByMachine);
    }
  }, [totalSVCHoursByMachine, appConstants]);

  const FilterDropdown = ({ label, value, options, onChange }) => (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      displayEmpty
      style={{ width: "100%", fontSize: "0.85rem", fontWeight: value ? "normal" : 500 }}
      renderValue={(selected) => selected || <span style={{ fontWeight: 550 }}>{label}</span>}
    >
      <MenuItem value="" style={{ fontWeight: "bold" }}>
        {label}
      </MenuItem>
      {(options || [])
        .slice()
        .sort((a, b) => String(a).localeCompare(String(b)))
        .map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
    </Select>
  );

  const filteredTickets = useMemo(() => {
    let tickets = serviceTicketMachine || [];

    if (filters.engineer) tickets = tickets.filter((t) => t.assignees?.split(",")[0]?.trim() === filters.engineer);
    if (filters.category) tickets = tickets.filter((t) => t.ticketCategory === filters.category);
    if (filters.status) tickets = tickets.filter((t) => t.status === filters.status);
    if (filters.supervisor) tickets = tickets.filter((t) => t.assignerId === filters.supervisor);

    return tickets;
  }, [serviceTicketMachine, filters]);

  const { engineers, categories, statuses, supervisors } = useMemo(() => {
    const engineersSet = new Set();
    const categoriesSet = new Set();
    const statusesSet = new Set();
    const supervisors = [];

    (filteredTickets || []).forEach((ticket) => {
      const prTechnician = ticket.assignees?.split(",")[0]?.trim();
      if (prTechnician) engineersSet.add(prTechnician);
      if (ticket.ticketCategory) categoriesSet.add(ticket.ticketCategory);
      if (ticket.status) statusesSet.add(ticket.status);

      const supervisor = appConstants?.appUsers?.find((user) => user.userId === ticket.assignerId);
      if (supervisor) {
        if (!supervisors.some((s) => s.id === supervisor.userId)) {
          supervisors.push({ id: supervisor.userId, name: supervisor.name });
        }
      }
    });

    return {
      engineers: Array.from(engineersSet).sort((a, b) => a.localeCompare(b)),
      categories: Array.from(categoriesSet).sort((a, b) => a.localeCompare(b)),
      statuses: Array.from(statusesSet).sort((a, b) => a.localeCompare(b)),
      supervisors: [...supervisors].sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [filteredTickets, appConstants]);

  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));
  const gridTicketEdit = (args) => {
    return (
      <div>
        {user.roles[0] === appConstants?.allTypes?.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR ? (
          <Link to={`/${Links.SERVICE_TICKET}/` + args.row.ticketId}>
            <IconButton sx={{ mr: 1 }}>
              <Tooltip title="View">
                <ViewIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
              </Tooltip>
            </IconButton>
          </Link>
        ) : null}
      </div>
    );
  };

  const renderAssignees = (args) => {
    const prTechnician = args.row.assignees?.split(",")[0] || " ";
    return <div>{prTechnician}</div>;
  };
  const renderSupervisors = (args) => {
    const supervisor = supervisors.find((s) => s.id === args.row.assignerId);
    return <div>{supervisor ? supervisor.name : " "}</div>;
  };

  const renderDescription = (data) => {
    return <div title={`${data?.row?.description || ""}`}>{`${data?.row?.description || ""}`}</div>;
  };
  const serviceTicketForMachineTableAttributes = [
    {
      field: "serviceTicketNumber",
      headerName: "Ticket ID",
      minWidth: 50,
      flex: 0.2,
      valueGetter: (params) => {
        return appConstants?.showServiceTicketId ? params.row.ticketId : params.row.serviceTicketNumber;
      },
      renderCell: (params) => {
        const displayValue = appConstants?.showServiceTicketId ? params.row.ticketId : params.value;
        return (
          <span
            style={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + params.row.ticketId)}
          >
            {displayValue}
          </span>
        );
      },
    },
    {
      field: "dateCreated",
      headerName: "Created",
      minWidth: 60,
      flex: 0.2,
      sortComparator: dateComparator,
      renderCell: (params) => moment(params.value).format("DD MMM YY"),
    },
    {
      field: "scheduledDate",
      headerName: "Sch. Date",
      minWidth: 60,
      flex: 0.2,
      sortComparator: dateComparator,
      renderCell: (params) => moment(params.value).format("DD MMM YY"),
    },
    {
      field: "ticketCategory",
      headerName: "Category",
      minWidth: 60,
      flex: 0.3,
      renderHeader: () => (
        <FilterDropdown
          label="Category"
          value={filters.category}
          options={categories}
          onChange={(value) => setFilters((prev) => ({ ...prev, category: value }))}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 60,
      flex: 0.3,
      renderHeader: () => (
        <FilterDropdown
          label="Status"
          value={filters.status}
          options={statuses}
          onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
        />
      ),
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 70,
      flex: 0.4,
      renderCell: renderDescription,
    },
    {
      field: "assignerId",
      headerName: "Supervisor",
      minWidth: 60,
      flex: 0.3,
      renderCell: renderSupervisors,
      renderHeader: () => (
        <Select
          value={filters.supervisor}
          onChange={(e) => setFilters((prev) => ({ ...prev, supervisor: e.target.value }))}
          displayEmpty
          style={{ width: "100%", fontSize: "0.85rem", fontWeight: filters.supervisor ? "normal" : "bold" }}
          renderValue={(selected) => {
            const selectedSupervisor = supervisors.find((s) => s.id === selected);
            return selectedSupervisor ? (
              selectedSupervisor.name
            ) : (
              <span style={{ fontSize: "0.85rem", fontWeight: "bold" }}>Supervisor</span>
            );
          }}
        >
          <MenuItem value="" style={{ fontWeight: "bold" }}>
            Supervisor
          </MenuItem>
          {[...supervisors]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((s) => (
              <MenuItem key={s.id} value={s.id} style={{ fontSize: "0.85rem" }}>
                {s.name}
              </MenuItem>
            ))}
        </Select>
      ),
    },
    {
      field: "assignees",
      headerName: "Technician",
      minWidth: 60,
      flex: 0.3,
      renderCell: renderAssignees,
      renderHeader: () => (
        <FilterDropdown
          label="Technician"
          value={filters.engineer}
          options={engineers}
          onChange={(value) => setFilters((prev) => ({ ...prev, engineer: value }))}
        />
      ),
    },
    // {
    //   field: "edit",
    //   headerName: "",
    //   minWidth: 60,
    //   flex: 0.2,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: gridTicketEdit,
    // },
  ];

  const downloadCsv = (e) => {
    e.preventDefault();
    const headers = ["Date Created,Ticket Number,Category,Description,Status,Techinicians(s)"];
    const keys = ["dateCreated", "ticketNumber", "ticketCategory", "description", "status", "assignees"];
    const fileName = `${initialFormData.maintenanceCode || ""}_Service_Tickets`;
    exportToCsv(headers, keys, serviceTickets, fileName || "Machine_Service_Tickets");
  };

  const handleViewFile = () => {
    setCurrentFilePathIndex(0);
    setShowDialogView(true);
  };

  const handleCloseDialogView = () => {
    setCurrentFilePathIndex(null);
    setShowDialogView(false);
  };

  const handleQRCodeClick = async () => {
    const data = `
        Code: ${initialFormData.maintenanceCode}
        Mfg: ${initialFormData.manufacturer}
        Model: ${initialFormData.model}
        SN: ${initialFormData.serialNumber}
        Location: ${initialFormData.department}
        `;

    const qrCodeApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(data)}`;

    try {
      const response = await fetch(qrCodeApiUrl);
      if (!response.ok) {
        console.error("Failed to generate QR code.");
        return;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = blobUrl;
      // const custName = appConstants?.appCustomerName
      //   ?.filter((customer) => customer.id == 5284)[0]
      //   ?.customerName.split(" ")
      //   .join("_");
      const custName = appConstants?.appCustomerName?.replace(/\s+/g, "_") || "Unknown_Customer";
      downloadLink.download = `${custName}_qr_code_machine_${initialFormData.maintenanceCode}.png`;

      downloadLink.click();

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  const renderLabel = (label, value) => (
    <Stack direction={"row"}>
      <Typography sx={{ fontWeight: "bold" }}>{label}</Typography>
      <Typography flexWrap={"wrap"} sx={{ ml: 0.5 }}>
        <span style={{ fontWeight: "bold", flexWrap: "wrap" }}>: </span> {value}
      </Typography>
    </Stack>
  );
  const serviceCategoryList =
    Object.values(appConstants?.allTypes?.SERVICE_CATEGORY)
      ?.filter((category) => category !== "Internal Audit" && category !== "Task")
      ?.sort((a, b) => a.localeCompare(b)) || [];
  const { ABBREVIATIONS } = appConstants?.allTypes || {};

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ backgroundColor: "white", border: "1px solid #ccc", padding: "10px", borderRadius: "5px" }}>
          <p style={{ margin: 0, fontWeight: "bold" }}>{payload[0]?.payload?.category || ""}</p>
          <p style={{ margin: 0, color: theme.palette.secondary[300] }}>Total Hours Spent : {payload[0].payload?.totalHourSpent || 0}</p>
        </div>
      );
    }

    return null;
  };

  const makeRenderBarValueLabel = (seriesIndex) => (props) => {
    const { x, y, width, value } = props;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;

    return (
      <text
        x={x + width / 2 + (seriesIndex % 2 === 0 ? -7 : 7)}
        y={y - 9 - (seriesIndex % 2) * 11}
        textAnchor="middle"
        fontSize={14}
        // fontWeight={600}
        fill={mode === "dark" ? "#fff" : "#333"}
        stroke={mode === "dark" ? "#000" : "#fff"}
        strokeWidth={3}
        paintOrder="stroke"
      >
        {numeric.toFixed(2)}
      </text>
    );
  };

  const renderTotalHoursLabelBlack = (props) => {
    const { x, y, width, value } = props;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;

    return (
      <text x={x + width / 2} y={y - 8} textAnchor="middle" fontSize={14} fill="#000">
        {numeric}
      </text>
    );
  };

  const handleMachineHistory = () => {
    setMachineHistoryLoading(true);
    dispatch(getMachineServiceHistory(id)).then((res) => {
      setMachineHistoryLoading(false);
      if (res?.meta?.requestStatus === "fulfilled") {
        toast.success("Machine History Sent Successfully.");
      } else {
        toast.error("Please try again!");
      }
    });
  };

  return (
    <>
      <Stack sx={{ ml: 2, mt: 2, mb: 1 }}>
        <Header
          titles={[
            "Assets",
            initialFormData.serialNumber
              ? `Maintenance Code: ${initialFormData.maintenanceCode || ""} | Manufacturer: ${initialFormData.manufacturer || ""
              } | Model: ${initialFormData.model || ""} | Serial Number: ${initialFormData.serialNumber || ""}`
              : "",
          ]}
        />
      </Stack>
      {
        <ViewImageVideoModal
          showDialogView={showDialogView}
          handleCloseDialogView={handleCloseDialogView}
          data={serviceTicketMessagesList}
          selectedFileIndex={currentFilePathIndex}
        />
      }
      <Box sx={{ mx: 2 }}>
        {enableEditing ? (
          <Paper sx={{ borderRadius: 2, mb: 1.5, p: 2 }}>
            {id && !initialFormData?.id ? (
              <div
                style={{
                  textAlign: "center",
                  paddingTop: 200,
                  paddingBottom: 200,
                }}
              >
                <CircularProgress color="secondary" />
              </div>
            ) : (
              <AddEditMachine initialFormData={initialFormData} setEnableEditing={setEnableEditing} />
            )}
          </Paper>
        ) : (
          <Paper sx={{ borderRadius: 2, my: 1, p: 2 }}>
            <Stack direction={"row"}>
              <Stack mr={3}>
                <Box sx={{}}>
                  {initialFormData.profileImage ? (
                    <AuthorizedImage path={initialFormData.profileImage || null} width={200} height={200} />
                  ) : (
                    <Card sx={{ borderRadius: 5 }}>
                      <Stack alignItems={"center"} p={3} gap={2}>
                        <Button disabled component="label" sx={mode === "dark" ? { color: "white" } : {}}>
                          <Typography>No Image</Typography>
                        </Button>
                      </Stack>
                    </Card>
                  )}
                </Box>
              </Stack>
              <Stack sx={{ flex: 1 }} ml={3} mr={3}>
                <Stack justifyContent={"space-between"} direction={"row"} flexWrap={"wrap"}>
                  <Stack rowGap={1}>
                    {renderLabel("Asset number ", initialFormData.assetNumber || "-")}
                    {renderLabel("Manufacturing year ", initialFormData.manufacturingYear)}
                  </Stack>
                  <Stack rowGap={1}>
                    {renderLabel("Machine type ", initialFormData.machineType || "-")}
                    {renderLabel("Location ", initialFormData.location)}
                  </Stack>
                  <Stack gap={2} direction={"row"} flexWrap={"wrap"}>
                    <Stack rowGap={1}>
                      <Tooltip title="View Image" placement="top">
                        <Button onClick={handleViewFile} variant="contained">
                          View images
                        </Button>
                      </Tooltip>
                      <Tooltip title="Edit" placement="bottom">
                        <Button onClick={() => setEnableEditing(true)} variant="contained">
                          Edit
                        </Button>
                      </Tooltip>
                    </Stack>
                    <Stack rowGap={1}>
                      <Tooltip title="Download QR" placement="top">
                        <Button
                          sx={{ minWidth: 170 }}
                          startIcon={<DownloadIcon />}
                          endIcon={<QRCodeIcon />}
                          variant="contained"
                          onClick={handleQRCodeClick}
                        >
                          Download QR
                          {/* <QRCodeIcon /> */}
                        </Button>
                      </Tooltip>
                      <Tooltip title="Export to csv" placement="bottom">
                        <Button
                          startIcon={<DownloadIcon sx={{ ml: -2 }} />}
                          sx={{ minWidth: 170 }}
                          autoCapitalize="none"
                          variant="contained"
                          onClick={(e) => downloadCsv(e)}
                        >
                          {` Export to csv`}
                        </Button>
                      </Tooltip>
                    </Stack>
                    <Stack rowGap={1}>
                      <Tooltip title="Machine History" placement="top">
                        <Button
                          variant="contained"
                          onClick={handleMachineHistory}
                          disabled={machineHistoryLoading}
                          endIcon={!machineHistoryLoading && <SendIcon />}
                        >
                          {machineHistoryLoading ? "Machine History..." : "Machine History"}
                        </Button>
                      </Tooltip>
                      {machineHistoryLoading && <LinearProgress />}
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        )}
        <div>
          <Stack direction={"row"} gap={1} my={1}>
            <Box flex={1}>
              {id ? (
                <Paper sx={{ p: 2, height: 550 }}>
                  <Stack alignItems={"center"}>
                    <Typography variant="h5" fontWeight={600} mb={1}>
                      Service Hours By Category
                    </Typography>
                  </Stack>
                  <div style={{ width: "100%", height: 550 }}>
                    {isLoading ? (
                      <GraphSkeleton height={500} />
                    ) : (
                      <ResponsiveContainer width="99%" height="90%">
                        <ComposedChart
                          data={totalSVCHours}
                          margin={{
                            top: 20,
                            right: 10,
                            bottom: 10,
                            left: 10,
                          }}
                        >
                          <CartesianGrid stroke="#d6d6d6" />
                          <XAxis dataKey="abbreviatedCategory" stroke={mode === "dark" ? "#fff" : "#000"}></XAxis>
                          <YAxis allowDecimals={false} stroke={mode === "dark" ? "#fff" : "#000"}>
                            <Label
                              style={{
                                textAnchor: "middle",
                              }}
                              angle={270}
                              value={"Total Hour Spent"}
                              stroke={mode === "dark" ? "#fff" : "#7F7F7F"}
                              dx={-15}
                            />
                          </YAxis>
                          <ChartTooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar
                            dataKey="totalHourSpent"
                            name="Total Hour Spent"
                            barSize={30}
                            fill={theme.palette.secondary[300]}
                            label={renderTotalHoursLabelBlack}
                            isAnimationActive={false}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </Paper>
              ) : null}
            </Box>

            <Box flex={1}>
              {id ? (
                <Paper sx={{ p: 2, height: 550 }}>
                  <div style={{ width: "100%", height: 550 }}>
                    <Stack alignItems={"center"}>
                      <Typography variant="h5" fontWeight={600} mb={1}>
                        Service Hours By Category And Year
                      </Typography>
                    </Stack>
                    {isLoading ? (
                      <GraphSkeleton height={500} />
                    ) : (
                      <ResponsiveContainer width="99%" height="90%">
                        <ComposedChart
                          data={totalSVCHoursByCategoryAndYear}
                          barGap={18}
                          barCategoryGap="40%"
                          margin={{
                            top: 20,
                            right: 10,
                            bottom: 10,
                            left: 10,
                          }}
                        >
                          <CartesianGrid stroke="#d6d6d6" />
                          <XAxis dataKey="year" stroke={mode === "dark" ? "#fff" : "#000"}></XAxis>
                          <YAxis allowDecimals={false} stroke={mode === "dark" ? "#fff" : "#000"}>
                            <Label
                              style={{
                                textAnchor: "middle",
                              }}
                              angle={270}
                              value={"Total Hour Spent"}
                              stroke={mode === "dark" ? "#fff" : "#7F7F7F"}
                              dx={-15}
                            />
                          </YAxis>
                          <ChartTooltip />
                          <Legend />
                          {serviceCategoryList.map((category, index) => (
                            <Bar
                              key={category}
                              dataKey={String(category)}
                              name={ABBREVIATIONS?.[category] || category}
                              barSize={10}
                              isAnimationActive={false}
                              fill={
                                index === 0
                                  ? "#5e5d5d"
                                  : index === 1
                                    ? "red"
                                    : index === 2
                                      ? "brown"
                                      : index === 3
                                        ? "olive"
                                        : index === 4
                                          ? "orange"
                                          : "#0047AB"
                              }
                            >
                              <LabelList dataKey={String(category)} content={makeRenderBarValueLabel(index)} />
                            </Bar>
                          ))}
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </Paper>
              ) : null}
            </Box>
          </Stack>
        </div>
        {id ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" fontWeight={600} mb={1}>
              Tickets
            </Typography>
            {isLoading ? (
              <TableSkeleton rows={8} columns={serviceTicketForMachineTableAttributes.length} />
            ) : (
              <DataGridTable
                rowId={"ticketId"}
                data={filteredTickets.filter((item) => Object.values(item).join(" ").toLowerCase().includes(searchValue.toLowerCase()))}
                columnAttributes={serviceTicketForMachineTableAttributes}
              />
            )}
          </Paper>
        ) : null}
      </Box>
    </>
  );
};
export default Machine;
